import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, qs, type Client, type Consultant, type Lookup, type Paged, type ProjectRow } from '../api';
import { ErrorAlert, Loading } from '../components/ui';
import { addDays, hm, money, todayIso } from '../format';

interface IndicatorsResponse {
  period: { from: string; to: string };
  indicators: {
    totals: Record<'sessions' | 'workMinutes' | 'clientMinutes' | 'sharedOnlyMinutes' | 'clientsServed' | 'bankMinutes' | 'billableMinutes' | 'pendingOverageMinutes' | 'monthlyMinutes' | 'fixedMinutes' | 'openCount' | 'openMinutes', number>;
    byExecutor: { consultantId: string; name: string; minutes: number; sessions: number; clients: number }[];
    byMonth: { month: string; minutes: number; sessions: number; clients: number }[];
    byClient: { clientId: string; name: string; minutes: number; sessions: number }[];
    byModality: { modality: string; minutes: number; sessions: number }[];
    byCategory: { categoryId: string; name: string; minutes: number }[];
    byArea: { areaId: string | null; name: string; minutes: number }[];
  };
  prices: {
    bankNew: { pricePerHour: string | null; totalMinutes: number; totalValue: string };
    bankPortfolio: { pricePerHour: string | null; totalMinutes: number; totalValue: string };
    avulso: { pricePerHour: string | null; totalMinutes: number; totalValue: string };
    monthly: { contractCode: string; clientName: string; label: string; minutes: number; value: string | null; pricePerHour: string | null }[];
    fixed: { contractCode: string; clientName: string; label: string; minutes: number; value: string | null; pricePerHour: string | null; provisional?: boolean }[];
    openingExcluded: number;
    formulas: Record<string, string>;
  };
}

type FilterState = { from: string; to: string; consultantId: string; ownerId: string; clientId: string; projectId: string; categoryId: string; areaId: string; modality: string };

const REPORTS = [
  { id: 'producao', title: 'Produção individual e mensal consolidada' },
  { id: 'horas-categorias', title: 'Horas por categoria, módulo e modalidade' },
  { id: 'preco-medio', title: 'Preço médio contratado' },
  { id: 'execucoes', title: 'Execuções por sessão e período' },
  { id: 'agenda', title: 'Agenda e pendências' },
  { id: 'projetos-contratos', title: 'Situação de projetos e contratos' },
  { id: 'faturaveis', title: 'Itens faturáveis' },
];

const MODALITY: Record<string, string> = { remoto: 'Remoto', presencial: 'Presencial' };
const monthStart = (d: string) => `${d.slice(0, 7)}-01`;
function monthEnd(d: string) {
  const [y, m] = d.split('-').map(Number) as [number, number];
  return new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10);
}
function presets() {
  const today = todayIso();
  const prevEnd = addDays(monthStart(today), -1);
  return [
    { label: 'Este mês', from: monthStart(today), to: today },
    { label: 'Mês anterior', from: monthStart(prevEnd), to: monthEnd(prevEnd) },
    { label: 'Últimos 3 meses', from: monthStart(addDays(monthStart(addDays(monthStart(today), -1)), -32)), to: today },
    { label: 'Este ano', from: `${today.slice(0, 4)}-01-01`, to: today },
  ];
}

// ------------------------------------------------------------------ marcas

// Barras horizontais de uma série: comprimento = magnitude; valor na ponta; dica com participação; tabela opcional.
function HBarChart({ title, rows, total, empty = 'Sem dados no período.' }: { title: string; rows: { key: string; label: string; minutes: number; extra?: string }[]; total?: number; empty?: string }) {
  const [table, setTable] = useState(false);
  const max = Math.max(1, ...rows.map((r) => r.minutes));
  const sum = total ?? rows.reduce((s, r) => s + r.minutes, 0);
  const share = (m: number) => (sum ? `${((m / sum) * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%` : '—');
  return (
    <section className="card chart-card">
      <div className="card-head">
        <h3>{title}</h3>
        {rows.length > 0 && (
          <button className="btn btn-sm btn-ghost" onClick={() => setTable((v) => !v)} aria-pressed={table}>
            {table ? 'Ver gráfico' : 'Ver tabela'}
          </button>
        )}
      </div>
      {rows.length === 0 ? (
        <p className="muted small">{empty}</p>
      ) : table ? (
        <table className="table">
          <thead>
            <tr>
              <th>Item</th>
              <th style={{ textAlign: 'right' }}>Horas</th>
              <th style={{ textAlign: 'right' }}>Participação</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key}>
                <td>{r.label}</td>
                <td style={{ textAlign: 'right' }}>{hm(r.minutes)}</td>
                <td style={{ textAlign: 'right' }}>{share(r.minutes)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="hbars" role="list" aria-label={title}>
          {rows.map((r) => (
            <div key={r.key} className="hbar-row" role="listitem" tabIndex={0} aria-label={`${r.label}: ${hm(r.minutes)} (${share(r.minutes)})`}>
              <span className="hbar-label">{r.label}</span>
              <span className="hbar-track">
                {/* Área reservada: a barra maior ocupa 100% dela e o valor sempre cabe à direita. */}
                <span className="hbar-area">
                  <span className="hbar-fill" style={{ width: `${Math.max(1, (r.minutes / max) * 100)}%` }} />
                  <span className="hbar-value" style={{ left: `calc(${Math.max(1, (r.minutes / max) * 100)}% + 6px)` }}>
                    {hm(r.minutes)}
                  </span>
                </span>
              </span>
              <span className="chart-tip" role="tooltip">
                <strong>{r.label}</strong>
                <br />
                {hm(r.minutes)} · {share(r.minutes)} do total{r.extra ? ` · ${r.extra}` : ''}
              </span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// Colunas por mês (mudança no tempo, uma série).
function MonthColumns({ rows }: { rows: { month: string; minutes: number; sessions: number }[] }) {
  const [table, setTable] = useState(false);
  const max = Math.max(1, ...rows.map((r) => r.minutes));
  const label = (m: string) => `${m.slice(5)}/${m.slice(2, 4)}`;
  return (
    <section className="card chart-card">
      <div className="card-head">
        <h3>Produção mensal consolidada (horas)</h3>
        {rows.length > 0 && (
          <button className="btn btn-sm btn-ghost" onClick={() => setTable((v) => !v)} aria-pressed={table}>
            {table ? 'Ver gráfico' : 'Ver tabela'}
          </button>
        )}
      </div>
      {rows.length === 0 ? (
        <p className="muted small">Sem produção confirmada no período.</p>
      ) : table ? (
        <table className="table">
          <thead>
            <tr>
              <th>Mês</th>
              <th style={{ textAlign: 'right' }}>Horas</th>
              <th style={{ textAlign: 'right' }}>Sessões</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.month}>
                <td>{label(r.month)}</td>
                <td style={{ textAlign: 'right' }}>{hm(r.minutes)}</td>
                <td style={{ textAlign: 'right' }}>{r.sessions}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="cols" role="list" aria-label="Produção mensal">
          {rows.map((r) => (
            <div key={r.month} className="col" role="listitem" tabIndex={0} aria-label={`${label(r.month)}: ${hm(r.minutes)}, ${r.sessions} sessões`}>
              <span className="col-value">{hm(r.minutes)}</span>
              <span className="col-bar" style={{ height: `${Math.max(2, (r.minutes / max) * 100)}%` }} />
              <span className="col-label">{label(r.month)}</span>
              <span className="chart-tip" role="tooltip">
                <strong>{label(r.month)}</strong>
                <br />
                {hm(r.minutes)} · {r.sessions} sessões
              </span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function Kpi({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div className="stat">
      <div className="label">{label}</div>
      <div className="value">{value}</div>
      {note && <div className="small muted">{note}</div>}
    </div>
  );
}

const price = (v: string | null) => (v === null ? 'não calculável' : `${money(v)}/h`);

// ------------------------------------------------------------------ página

export function IndicatorsPanel() {
  const ps = presets();
  const [f, setF] = useState<FilterState>({ from: ps[0]!.from, to: ps[0]!.to, consultantId: '', ownerId: '', clientId: '', projectId: '', categoryId: '', areaId: '', modality: '' });
  const set = (k: keyof FilterState, v: string) => setF((x) => ({ ...x, [k]: v }));
  const query = qs(f);
  const consultants = useQuery({ queryKey: ['consultants'], queryFn: () => api.get<{ data: Consultant[] }>('/consultants') });
  const clients = useQuery({ queryKey: ['clients', 'all-min'], queryFn: () => api.get<Paged<Client>>('/clients?pageSize=200') });
  const projects = useQuery({ queryKey: ['projects', 'all-min'], queryFn: () => api.get<Paged<ProjectRow>>('/projects?scope=all&pageSize=200') });
  const categories = useQuery({ queryKey: ['categories'], queryFn: () => api.get<{ data: Lookup[] }>('/categories') });
  const areas = useQuery({ queryKey: ['areas'], queryFn: () => api.get<{ data: Lookup[] }>('/areas') });
  const q = useQuery({ queryKey: ['indicators', query], queryFn: () => api.get<IndicatorsResponse>(`/indicators${query}`), placeholderData: (p) => p });
  const d = q.data;
  const t = d?.indicators.totals;
  const projOptions = useMemo(() => (projects.data?.data ?? []).filter((p) => !f.clientId || p.clientId === f.clientId), [projects.data, f.clientId]);

  return (
    <>
      <div className="filters-bar" role="group" aria-label="Filtros dos indicadores">
        <div className="segmented" role="group" aria-label="Período">
          {ps.map((p) => (
            <button key={p.label} aria-pressed={f.from === p.from && f.to === p.to} onClick={() => setF((x) => ({ ...x, from: p.from, to: p.to }))}>
              {p.label}
            </button>
          ))}
        </div>
        <label className="visually-hidden" htmlFor="i-from">De</label>
        <input id="i-from" type="date" value={f.from} onChange={(e) => set('from', e.target.value)} />
        <label className="visually-hidden" htmlFor="i-to">Até</label>
        <input id="i-to" type="date" value={f.to} onChange={(e) => set('to', e.target.value)} />
        <select aria-label="Executor" value={f.consultantId} onChange={(e) => set('consultantId', e.target.value)}>
          <option value="">Todos os executores</option>
          {consultants.data?.data.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <select aria-label="Responsável habitual" value={f.ownerId} onChange={(e) => set('ownerId', e.target.value)}>
          <option value="">Todos os responsáveis</option>
          {consultants.data?.data.map((c) => (
            <option key={c.id} value={c.id}>
              Resp.: {c.name}
            </option>
          ))}
        </select>
        <select aria-label="Cliente" value={f.clientId} onChange={(e) => setF((x) => ({ ...x, clientId: e.target.value, projectId: '' }))}>
          <option value="">Todos os clientes</option>
          {clients.data?.data.map((c) => (
            <option key={c.id} value={c.id}>
              {c.tradeName || c.name}
            </option>
          ))}
        </select>
        <select aria-label="Projeto" value={f.projectId} onChange={(e) => set('projectId', e.target.value)}>
          <option value="">Todos os projetos</option>
          {projOptions.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
        <select aria-label="Categoria" value={f.categoryId} onChange={(e) => set('categoryId', e.target.value)}>
          <option value="">Todas as categorias</option>
          {categories.data?.data.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <select aria-label="Módulo/área" value={f.areaId} onChange={(e) => set('areaId', e.target.value)}>
          <option value="">Todos os módulos</option>
          {areas.data?.data.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
        <select aria-label="Modalidade" value={f.modality} onChange={(e) => set('modality', e.target.value)}>
          <option value="">Remoto e presencial</option>
          <option value="remoto">Remoto</option>
          <option value="presencial">Presencial</option>
        </select>
      </div>

      <ErrorAlert error={q.error} />
      {q.isLoading || !d || !t ? (
        <Loading />
      ) : (
        <>
          <p className="small muted">
            Produção = execuções confirmadas e não anuladas, pela data do serviço, atribuídas a quem executou. Não inclui recebimentos, custos ou margens. Rascunhos e pendentes:{' '}
            {t.openCount} ({hm(t.openMinutes)}), fora dos números abaixo.
          </p>
          <div className="grid grid-4 grid-stats" style={{ marginBottom: '1rem' }}>
            <Kpi label="Horas executadas" value={hm(t.workMinutes)} note="trabalho dos consultores" />
            <Kpi label="Horas atribuídas ao cliente" value={hm(t.clientMinutes)} note={t.sharedOnlyMinutes ? `${hm(t.sharedOnlyMinutes)} só produção (sessão compartilhada)` : undefined} />
            <Kpi label="Sessões" value={t.sessions.toLocaleString('pt-BR')} />
            <Kpi label="Clientes atendidos" value={t.clientsServed.toLocaleString('pt-BR')} />
            <Kpi label="Cobertas por banco" value={hm(t.bankMinutes)} />
            <Kpi label="Faturáveis (avulso e excedente)" value={hm(t.billableMinutes)} />
            <Kpi label="Mensalidade / valor fechado" value={`${hm(t.monthlyMinutes)} / ${hm(t.fixedMinutes)}`} />
            <Kpi label="Excedente aguardando decisão" value={hm(t.pendingOverageMinutes)} />
          </div>

          <div className="grid grid-2">
            <HBarChart title="Produção individual (horas por executor)" rows={d.indicators.byExecutor.map((r) => ({ key: r.consultantId, label: r.name, minutes: r.minutes, extra: `${r.sessions} sessões, ${r.clients} clientes` }))} />
            <MonthColumns rows={d.indicators.byMonth} />
            <HBarChart title="Horas por categoria (rateio)" rows={d.indicators.byCategory.map((r) => ({ key: r.categoryId, label: r.name, minutes: r.minutes })).sort((a, b) => b.minutes - a.minutes)} />
            <HBarChart title="Horas por módulo/área (rateio)" rows={d.indicators.byArea.map((r) => ({ key: r.areaId ?? 'sem', label: r.name, minutes: r.minutes })).sort((a, b) => b.minutes - a.minutes)} />
            <HBarChart title="Horas por modalidade" rows={d.indicators.byModality.map((r) => ({ key: r.modality, label: MODALITY[r.modality] ?? r.modality, minutes: r.minutes, extra: `${r.sessions} sessões` }))} />
            <HBarChart title="Horas por cliente" rows={d.indicators.byClient.map((r) => ({ key: r.clientId, label: r.name, minutes: r.minutes, extra: `${r.sessions} sessões` })).sort((a, b) => b.minutes - a.minutes).slice(0, 15)} />
          </div>

          <section className="card">
            <h2>Preço médio contratado</h2>
            <p className="small muted">{d.prices.formulas.weighted} {d.prices.formulas.zero}</p>
            <div className="grid grid-3">
              <Kpi label="Bancos — novas contratações no período" value={price(d.prices.bankNew.pricePerHour)} note={`${money(d.prices.bankNew.totalValue)} ÷ ${hm(d.prices.bankNew.totalMinutes)}`} />
              <Kpi label="Bancos — carteira vigente" value={price(d.prices.bankPortfolio.pricePerHour)} note={`${money(d.prices.bankPortfolio.totalValue)} ÷ ${hm(d.prices.bankPortfolio.totalMinutes)}`} />
              <Kpi label="Avulso — horas faturadas" value={price(d.prices.avulso.pricePerHour)} note={`${money(d.prices.avulso.totalValue)} ÷ ${hm(d.prices.avulso.totalMinutes)}`} />
            </div>
            {d.prices.openingExcluded > 0 && <p className="small muted">{d.prices.openingExcluded} saldo(s) de abertura sem valor atribuído ficaram fora do cálculo.</p>}
            <div className="grid grid-2" style={{ marginTop: '1rem' }}>
              <div>
                <h3>Mensalidades</h3>
                <p className="small muted">{d.prices.formulas.monthly}</p>
                {d.prices.monthly.length === 0 ? (
                  <p className="small muted">Nenhuma mensalidade no período.</p>
                ) : (
                  <div className="table-wrap">
                  <table className="table">
                    <tbody>
                      {d.prices.monthly.map((r) => (
                        <tr key={r.contractCode + r.label}>
                          <td>
                            {r.clientName} <span className="small muted">{r.contractCode} · {r.label}</span>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            {money(r.value)} ÷ {hm(r.minutes)}
                          </td>
                          <td style={{ textAlign: 'right', fontWeight: 600 }}>{price(r.pricePerHour)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                )}
              </div>
              <div>
                <h3>Valor fechado</h3>
                <p className="small muted">{d.prices.formulas.fixed}</p>
                {d.prices.fixed.length === 0 ? (
                  <p className="small muted">Nenhum projeto de valor fechado.</p>
                ) : (
                  <div className="table-wrap">
                  <table className="table">
                    <tbody>
                      {d.prices.fixed.map((r) => (
                        <tr key={r.contractCode}>
                          <td>
                            {r.clientName} <span className="small muted">{r.contractCode}</span> {r.provisional && <span className="badge badge-demo">provisório</span>}
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            {money(r.value)} ÷ {hm(r.minutes)}
                          </td>
                          <td style={{ textAlign: 'right', fontWeight: 600 }}>{price(r.pricePerHour)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                )}
              </div>
            </div>
            <p className="small muted">Produção e preço médio não determinam distribuição de receita ou lucro entre os sócios.</p>
          </section>

          <section className="card">
            <h2>Relatórios com estes filtros</h2>
            <p className="small muted">Documentos internos. Cada arquivo mostra os filtros, a data de geração e os critérios usados.</p>
            <div className="list">
              {REPORTS.map((r) => (
                <div key={r.id} className="list-item report-row">
                  <span className="title">{r.title}</span>
                  <span className="actions">
                    <a className="btn btn-sm" href={`/api/v1/reports/${r.id}/pdf${query}`} target="_blank" rel="noopener">
                      PDF
                    </a>
                    <a className="btn btn-sm" href={`/api/v1/reports/${r.id}/html${query}`} target="_blank" rel="noopener">
                      HTML
                    </a>
                    <a className="btn btn-sm" href={`/api/v1/reports/${r.id}/xlsx${query}`}>
                      Excel
                    </a>
                    <a className="btn btn-sm btn-ghost" href={`/api/v1/reports/${r.id}/csv${query}`}>
                      CSV
                    </a>
                  </span>
                </div>
              ))}
            </div>
          </section>
        </>
      )}
    </>
  );
}
